import { Component } from '@theme/component';

const CANDIDATES = 'sol-photo-carousel, sol-drawer-carousel, slideshow-component';
const EXCLUDED = 'sol-reviews, .sol-reviews-section, .card-gallery, zoom-dialog, .dialog-zoomed-gallery, predictive-search-component, .predictive-search-results__wrapper, announcement-bar-component, .announcement-bar';

/** Read the existing rail; never replace its native slideshow or iPhone touch owner. */
class SolGalleryPagination extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.schedule();
  }

  initialize() {
    this.cleanup();
    if (!this.gallery || !this.track) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.className = 'sol-apple-gallery-pagination';
    this.setAttribute('data-sol-pagination', this.kind);
    if (this.kind === 'drawer') this.setAttribute('aria-hidden', 'true');
    this.label = document.querySelector('#sol-gallery-copy')?.dataset.slideLabel || '';
    this.originalTabindex = this.track.getAttribute('tabindex');
    if (this.originalTabindex === null || this.originalTabindex === '-1') this.track.tabIndex = 0;
    this.track.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
    this.track.addEventListener('keydown', event => this.onTrackKey(event), { signal });
    this.track.addEventListener('load', () => this.schedule(true), { capture: true, signal });
    this.gallery.closest('details')?.addEventListener('toggle', () => this.schedule(true), { signal });
    this.addEventListener('click', event => {
      const button = event.target instanceof Element ? event.target.closest('[data-sol-gallery-stop]') : null;
      if (button) this.go(Number(button.dataset.solGalleryStop));
    }, { signal });
    this.addEventListener('keydown', event => this.onDotKey(event), { signal });
    this.resize = new ResizeObserver(() => this.schedule(true));
    this.resize.observe(this.track);
    this.mutations = new MutationObserver(() => this.schedule(true));
    this.mutations.observe(this.track, { childList: true, attributes: true, subtree: true, attributeFilter: ['hidden', 'reveal'] });
    this.disabledObserver = new MutationObserver(() => this.schedule(true));
    this.disabledObserver.observe(this.gallery, { attributes: true, attributeFilter: ['disabled', 'mobile-disabled'] });
    // Scope keyboard focus to the merchant's homepage product carousel.
    if (this.kind === 'resources' && this.gallery.closest('[id$="__product_list_YidTcf"]')) {
      this.resourceTabindexes = new Map();
      this.focusObserver = new MutationObserver(() => this.syncResourceFocus());
      this.focusObserver.observe(this.track, { childList: true, attributes: true, subtree: true, attributeFilter: ['aria-hidden'] });
      this.syncResourceFocus();
    }
    this.update(true);
  }

  getItems() {
    return Array.from(this.track.children).filter(item =>
      item instanceof HTMLElement && item.querySelector('img') && (!item.hidden || item.hasAttribute('reveal'))
    );
  }

  schedule(geometry = false) {
    this.geometryDirty = this.geometryDirty || geometry;
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
  }

  update(geometry = false) {
    if (!this.isConnected || !this.track) return;
    if (geometry || this.geometryDirty || !this.stops) this.measure();
    if (this.hidden || !this.stops?.length) return;
    const current = Math.abs(this.track.scrollLeft);
    const stops = this.stops;
    let nearest = 0;
    stops.forEach((stop, index) => {
      if (Math.abs(stop.position - current) < Math.abs(stops[nearest].position - current)) nearest = index;
    });
    if (this.current === nearest) return;
    this.current = nearest;
    Array.from(this.children).forEach((dot, index) => {
      dot.classList.toggle('is-current', index === nearest);
      if (this.kind === 'drawer') return;
      dot.tabIndex = index === nearest ? 0 : -1;
      if (index === nearest) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  measure() {
    this.geometryDirty = false;
    const items = this.getItems();
    const disabled = this.gallery.getAttribute('disabled') === 'true' ||
      (this.gallery.hasAttribute('mobile-disabled') && matchMedia('(max-width: 749px)').matches);
    this.gallery.dataset.solGalleryMultiple = String(items.length > 1 && !disabled);
    const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    this.hidden = disabled || items.length < 2 || max < 3 || this.track.clientWidth === 0;
    if (this.hidden) return;
    const bounds = this.track.getBoundingClientRect();
    const style = getComputedStyle(this.track);
    this.rtl = style.direction === 'rtl';
    const current = Math.abs(this.track.scrollLeft);
    const padding = parseFloat(this.rtl ? style.paddingRight : style.paddingLeft) || 0;
    const stops = [];
    items.forEach((item, index) => {
      const rect = item.getBoundingClientRect();
      const offset = this.rtl ? bounds.right - rect.right : rect.left - bounds.left;
      const position = Math.max(0, Math.min(max, current + offset - padding));
      // Wide rows have fewer reachable positions than images. Avoid duplicate end dots.
      if (!stops.length || Math.abs(position - stops[stops.length - 1].position) > 3) stops.push({ position, index });
    });
    this.items = items;
    this.stops = stops;
    const renderKey = `${items.length}:${stops.map(stop => stop.index).join(',')}`;
    if (this.renderKey !== renderKey) {
      this.renderDots(stops);
      this.renderKey = renderKey;
    }
    this.current = -1;
  }

  renderDots(stops) {
    const fragment = document.createDocumentFragment();
    stops.forEach((stop, index) => {
      const dot = document.createElement(this.kind === 'drawer' ? 'span' : 'button');
      dot.className = 'sol-apple-gallery-dot';
      if (this.kind !== 'drawer') {
        dot.type = 'button';
        dot.dataset.solGalleryStop = String(index);
        dot.setAttribute('aria-label', this.label.replace('__INDEX__', String(stop.index + 1)).replace('__LENGTH__', String(this.items.length)));
      }
      fragment.append(dot);
    });
    this.replaceChildren(fragment);
  }

  go(index) {
    this.update();
    const stop = this.stops?.[Math.max(0, Math.min(this.stops.length - 1, index))];
    if (!stop) return;
    if (this.gallery.matches('slideshow-component') && typeof this.gallery.select === 'function') {
      // Native selection keeps zoom, variants, aria-hidden, thumbnails and autoplay in sync.
      this.gallery.select(stop.index, undefined, { animate: !this.motion.matches });
    } else {
      this.track.scrollTo({ left: this.rtl ? -stop.position : stop.position, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
  }

  onTrackKey(event) {
    // SOL's existing listener already owns Left/Right; respect its preventDefault.
    if (event.defaultPrevented || event.target !== this.track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    this.update();
    if (this.hidden) return;
    event.preventDefault();
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.stops.length - 1 :
      this.current + ((event.key === 'ArrowRight') !== this.rtl ? 1 : -1);
    this.go(next);
  }

  onDotKey(event) {
    if (this.kind === 'drawer' || !(event.target instanceof HTMLButtonElement) || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const index = Number(event.target.dataset.solGalleryStop);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.stops.length - 1 :
      index + ((event.key === 'ArrowRight') !== this.rtl ? 1 : -1);
    const target = Math.max(0, Math.min(this.stops.length - 1, next));
    this.go(target);
    this.children[target]?.focus({ preventScroll: true });
  }

  syncResourceFocus() {
    if (!this.resourceTabindexes || !this.track) return;
    const hiddenSlides = Array.from(this.track.children).filter(slide => slide.getAttribute('aria-hidden') === 'true');
    const hiddenTargets = new Set(hiddenSlides.flatMap(slide => Array.from(slide.querySelectorAll('a[href], area[href], button, input, select, textarea, summary, [tabindex], [contenteditable]'))));
    for (const [element, tabindex] of this.resourceTabindexes) {
      if (hiddenTargets.has(element)) continue;
      if (tabindex === null) element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', tabindex);
      this.resourceTabindexes.delete(element);
    }
    for (const element of hiddenTargets) {
      if (!this.resourceTabindexes.has(element)) this.resourceTabindexes.set(element, element.getAttribute('tabindex'));
      element.setAttribute('tabindex', '-1');
    }
    if (hiddenSlides.some(slide => slide.contains(document.activeElement))) this.track.focus({ preventScroll: true });
  }

  cleanup() {
    this.focusObserver?.disconnect();
    for (const [element, tabindex] of this.resourceTabindexes || []) {
      if (tabindex === null) element.removeAttribute('tabindex');
      else element.setAttribute('tabindex', tabindex);
    }
    this.resourceTabindexes?.clear();
    this.controller?.abort();
    this.resize?.disconnect();
    this.mutations?.disconnect();
    this.disabledObserver?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  disconnectedCallback() {
    this.cleanup();
    if (this.track && this.originalTabindex !== undefined) {
      if (this.originalTabindex === null) this.track.removeAttribute('tabindex');
      else this.track.setAttribute('tabindex', this.originalTabindex);
    }
    super.disconnectedCallback();
  }
}

if (!customElements.get('sol-gallery-pagination')) customElements.define('sol-gallery-pagination', SolGalleryPagination);

function enhance(gallery) {
  if (gallery.querySelector(':scope > sol-gallery-pagination') || gallery.closest(EXCLUDED)) return;
  let kind = gallery.matches('sol-drawer-carousel') ? 'drawer' : gallery.matches('sol-photo-carousel') ? 'photos' : '';
  const track = kind ? gallery.querySelector('[data-sol-carousel-track], .menu-drawer__featured-content-list') :
    gallery.querySelector(':scope > slideshow-container > slideshow-slides');
  if (!track) return;
  const slides = Array.from(track.children);
  if (!slides.length || slides.some(slide => !slide.querySelector('img'))) return;
  if (!kind) {
    if (gallery.parentElement?.closest('slideshow-component') || gallery.getAttribute('disabled') === 'true') return;
    if (gallery.closest('media-gallery')) {
      if (slides.some(slide => !slide.matches('.product-media-container--image'))) return;
      kind = 'product';
    } else if (gallery.classList.contains('resource-list__carousel')) kind = 'resources';
    else {
      if (slides.some(slide => slide.textContent.trim() || slide.querySelector('video, deferred-media, product-model, model-viewer, input, select, textarea, form'))) return;
      kind = 'images';
    }
  }
  gallery.classList.add('sol-apple-gallery');
  gallery.dataset.solGalleryKind = kind;
  track.classList.add('sol-apple-gallery-track');
  const pagination = document.createElement('sol-gallery-pagination');
  pagination.gallery = gallery;
  pagination.track = track;
  pagination.kind = kind;
  gallery.append(pagination);
}

function scan(root) {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (root instanceof Element && root.matches(CANDIDATES)) enhance(root);
  root.querySelectorAll(CANDIDATES).forEach(enhance);
}

scan(document);
// Native product variant replacement and Shopify section rendering may add new galleries.
const discovery = new MutationObserver(records => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element) || node.matches('sol-gallery-pagination, .sol-apple-gallery-dot')) continue;
      if (node.matches(CANDIDATES) || node.querySelector(CANDIDATES)) scan(node);
    }
  }
});
discovery.observe(document.body, { childList: true, subtree: true });
window.addEventListener('pagehide', event => { if (!event.persisted) discovery.disconnect(); }, { once: true });
